import { asDecimal, hasValue } from '@/lib/helpers/numeric'
import tailwind from '@/lib/tailwind'

/**
 * Round angle to specified decimals
 */
const roundAngle = (angle) => asDecimal(angle, 1)

/**
 * Determine if angle is left or right of center
 *
 * @param {number} angle - Angle value
 * @returns {string} - 'left', 'right', or 'center'
 */
export const leftOrRight = (angle) => {
    const angleRounded = roundAngle(angle)
    return angleRounded === 0 ? 'center' : angle < 0 ? 'left' : 'right'
}

/**
 * Determine if angle is front or back of center
 *
 * @param {number} angle - Angle value
 * @returns {string} - 'front', 'back', or 'center'
 */
export const frontOrBack = (angle) => {
    const angleRounded = roundAngle(angle)
    return angleRounded === 0 ? 'center' : angle < 0 ? 'front' : 'back'
}

/**
 * Format angle with degree symbol
 *
 * @param {number} angle - Angle value
 * @param {boolean} asAbsolute - Use absolute value
 * @returns {string}
 */
export const formatAngle = (angle, asAbsolute = true) => {
    return roundAngle(asAbsolute ? Math.abs(angle) : angle) + '°'
}

/**
 * Convert watts to joules
 *
 * @param {number} watts - Watts value
 * @param {number} decimals - Number of decimals
 * @returns {number}
 */
export const wattsToJoules = (watts, decimals = 2) => asDecimal(watts / 100, decimals)

/**
 * Convert watts to kilocalories
 *
 * @param {number} watts - Watts value
 * @param {number} decimals - Number of decimals
 * @returns {number}
 */
export const wattsToKiloCalories = (watts, decimals = 2) => asDecimal(watts / 100 / 4184, decimals)

/**
 * Convert joules to kilocalories
 *
 * @param {number} joules - Joules value
 * @param {number} decimals - Number of decimals
 * @returns {number}
 */
export const joulesToKiloCalories = (joules, decimals = 2) => {
    if (!joules || isNaN(joules)) return 0
    return asDecimal(joules / 4184, decimals)
}

/**
 * Get color from gradient based on value/max ratio
 *
 * @param {number} value - Current value
 * @param {number} max - Maximum value
 * @returns {string|undefined} - Color from gradient
 */
export const getColor = (value, max) => {
    const gradientLength = Object.keys(tailwind.theme.color.gradient).length

    if (value === null || value === undefined || !hasValue(max) || max === 0) {
        return
    }

    value = value ? Math.abs(value) : 0
    let i = value === 0 ? 0 : Math.floor((value * gradientLength) / max)

    if (i >= gradientLength) i = gradientLength - 1

    return tailwind.theme.color.gradient[i + 1]
}
